import type { ProviderLevel } from '@ems/clinical';
import { ChevronRight, Pill, Search, ShieldAlert, Syringe } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router';
import { Alert, Badge, ButtonLink, EmptyState, Input, PageHeader, QueryError, Spinner } from '@/components/ui';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';
import { useMedications, type AppMedication } from './api';

export function MedicationBadges({ med, level }: { med: AppMedication; level?: ProviderLevel | null }) {
  return (
    <>
      {med.highAlert && (
        <Badge tone="danger">
          <ShieldAlert className="size-3" /> High alert
        </Badge>
      )}
      {med.controlledSchedule && <Badge tone="purple">C-{med.controlledSchedule}</Badge>}
      {med.isCustomized && <Badge tone="brand">Agency</Badge>}
      {med.isSample && !med.formulary?.reviewed_at && <Badge tone="warning">Sample</Badge>}
      {med.formulary?.reviewed_at && <Badge tone="success">MD reviewed</Badge>}
      {level && med.scope.length > 0 && !med.scope.includes(level) && <Badge tone="neutral">Outside your scope</Badge>}
    </>
  );
}

export function MedsPage() {
  const { data, isPending, isError, error, refetch } = useMedications();
  const { activeMembership, profile } = useAuth();
  const [query, setQuery] = useState('');
  const [highAlertOnly, setHighAlertOnly] = useState(false);
  const level = (activeMembership?.certification_level ?? profile?.certification_level) as ProviderLevel | null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (data?.medications ?? [])
      .filter((m) => !highAlertOnly || m.highAlert)
      .filter((m) => !q || [m.genericName, ...m.brandNames, m.drugClass, ...m.indications].some((s) => s.toLowerCase().includes(q)));
  }, [data, query, highAlertOnly]);

  return (
    <div>
      <PageHeader
        title="Medications"
        subtitle={data?.source === 'organization' ? `${activeMembership?.organization.name} formulary` : 'Reference formulary'}
        actions={
          <ButtonLink to="/tools/dosing" size="sm" variant="outline" icon={<Syringe className="size-4" />}>
            Dosing calculator
          </ButtonLink>
        }
      />
      {data?.source === 'bundled' && (
        <Alert tone="warning" className="mb-4" title="Offline reference formulary">
          Sign in to see your agency’s medical-director-approved formulary. This built-in sample data has not been reviewed for your system.
        </Alert>
      )}
      <div className="mb-5 flex flex-wrap gap-2">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 size-4 -translate-y-1/2 text-stone-400" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Name, brand, class, or indication" className="h-12 pl-10 text-base" aria-label="Filter medications" />
        </div>
        <button
          type="button"
          aria-pressed={highAlertOnly}
          onClick={() => setHighAlertOnly(!highAlertOnly)}
          className={cn('inline-flex h-12 items-center gap-1.5 rounded-md border px-3 text-sm font-medium transition-colors', highAlertOnly ? 'border-red-700 bg-red-700 text-white' : 'border-stone-300 bg-white text-stone-700 hover:border-stone-500 hover:text-stone-950 dark:border-stone-700 dark:bg-ink-900 dark:text-stone-300 dark:hover:border-stone-500 dark:hover:text-white')}
        >
          <ShieldAlert className="size-4" />
          High alert
        </button>
      </div>

      {isPending ? (
        <Spinner />
      ) : isError && !data ? (
        <QueryError error={error} retry={() => refetch()} />
      ) : filtered.length === 0 ? (
        <EmptyState icon={<Pill className="size-10" />} title="No medications match" />
      ) : (
        <ul className="divide-y divide-stone-200 border-y border-stone-200 dark:divide-stone-800 dark:border-stone-800">
          {filtered.map((m) => (
            <li key={m.slug}>
              <Link to={`/meds/${m.slug}`} className="flex items-center gap-3 px-1 py-3 transition-colors hover:bg-stone-100 dark:hover:bg-ink-900">
                <span className="min-w-0 flex-1">
                  <span className="flex flex-wrap items-center gap-1.5">
                    <span className="font-medium">{m.genericName}</span>
                    <MedicationBadges med={m} level={level} />
                  </span>
                  <span className="block truncate text-sm text-stone-500 dark:text-stone-400">
                    {[m.brandNames.join(', '), m.drugClass].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <ChevronRight className="size-4 shrink-0 text-stone-300 dark:text-stone-600" />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
